import {apiRequest} from './api';
import {
  deleteEventEndpoint,
  deletePlaceEndpoint,
  insertPlaceEndpoint,
  organizerByIdEndpoint,
  placeDetailsForEditEndpoint,
  updatePlaceEndpoint,
} from '../constants/endpoints';
import {OrganizerDto} from '../../generated/models/OrganizerDto';

export interface PlaceAddress {
  street?: string | null;
  streetNumber?: string | null;
  city?: string | null;
  county?: string | null;
  country?: string | null;
  postalCode?: string | null;
  countryIsoCode?: string | null;
}

export interface PlaceOpeningHour {
  // 0 = Sunday ... 6 = Saturday
  day: number;
  // "HH:mm:ss"
  openingTime: string;
  closingTime: string;
}

// Everything the place endpoints read and write. Kept loose on purpose: the edit
// screen sends back whatever the server returned, plus the fields it changed, so
// a field this UI does not know about is never dropped on save.
export interface PlacePayload {
  name: string;
  description?: string | null;
  address?: PlaceAddress | null;
  location: {latitude?: number; longitude?: number};
  photos?: {photoRoute?: string | null}[] | null;
  tags: number[];
  mapCdnPath?: string | null;
  internationalPhoneNumber?: string | null;
  googleRating?: number | null;
  googleMapsUri?: string | null;
  openingHours?: PlaceOpeningHour[] | null;
  priceLevel?: string | null;
  userRatingCount?: number;
  isOperational?: boolean;
  locationCode?: number | null;
  [key: string]: unknown;
}

// The API returns some payloads bare under `data` and others wrapped as
// `data.value` (the Result envelope). Accept either.
const unwrap = <T>(response: unknown): T | null => {
  const data = (response as {data?: unknown} | null)?.data;
  if (data && typeof data === 'object' && 'value' in data) {
    return ((data as {value?: T}).value ?? null) as T | null;
  }
  return (data as T | undefined) ?? null;
};

// apiRequest swallows errors and returns null, which is also what a successful
// empty response looks like, so failures are tracked through onError.
const request = async <T>(
  payload: Parameters<typeof apiRequest>[0],
  message: string,
): Promise<T | null> => {
  let failure: Error | null = null;

  const response = await apiRequest<unknown, unknown>(payload as never, {
    errorMessage: message,
    onError: (error: unknown) => {
      const detail = error instanceof Error ? error.message : '';
      failure = new Error(detail ? `${message}: ${detail}` : message);
    },
  });

  if (failure) {
    throw failure;
  }

  return unwrap<T>(response);
};

export const fetchPlaceForEdit = async (
  placeId: string,
): Promise<PlacePayload> => {
  const place = await request<PlacePayload>(
    {endpoint: placeDetailsForEditEndpoint(placeId), method: 'GET'},
    'Could not load the place',
  );

  if (!place) {
    throw new Error('Could not load the place');
  }

  return place;
};

export const createPlace = (organizerId: string, place: PlacePayload) =>
  request(
    {endpoint: insertPlaceEndpoint(organizerId), method: 'POST', body: place},
    'Could not create the place',
  );

export const updatePlace = (placeId: string, place: PlacePayload) =>
  request(
    {endpoint: updatePlaceEndpoint(placeId), method: 'PATCH', body: place},
    'Could not save the place',
  );

export const deletePlace = (placeId: string) =>
  request(
    {endpoint: deletePlaceEndpoint(placeId), method: 'DELETE'},
    'Could not delete the place',
  );

// The route needs an organizer id, but the server only uses the event id, so an
// event without an organizer can still be deleted.
const NO_ORGANIZER = '00000000-0000-0000-0000-000000000000';

export const deleteEvent = (eventId: string, organizerId?: string | null) =>
  request(
    {
      endpoint: deleteEventEndpoint(organizerId || NO_ORGANIZER, eventId),
      method: 'DELETE',
    },
    'Could not delete the event',
  );

export const fetchOrganizer = async (
  organizerId: string,
): Promise<OrganizerDto> => {
  const organizer = await request<OrganizerDto>(
    {endpoint: organizerByIdEndpoint(organizerId), method: 'GET'},
    'Could not load the organizer',
  );

  if (!organizer) {
    throw new Error('Could not load the organizer');
  }

  return organizer;
};
